/**
 * DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 -- THE C2 ACCEPTED-STATE TRUTH
 * OWNER (SOURCE-OF-TRUTH-DRIFT corrective; Tower live-browser doctor walk
 * 2026-08-14).
 *
 * THE WALKED DEFECT: three owners spoke one fact on one screen -- the plan
 * line chip said "Accepted - planned" (live sheet), the journey header
 * counter said "0 accepted" (a stale journey wire fetched before the
 * decision), and the Estimation column said "Nothing accepted yet" (the
 * accepted-VALUE sum collapsing a zero-fee accepted line into "nothing
 * accepted") -- and the split persisted after reload and on the locked
 * record.
 *
 * THIS MODULE IS THE ONE SEMANTIC OWNER of accepted-state display on the
 * consult surface. The line chip, the journey header counters, and the
 * Estimation summary cell ALL consume these derivations over the LIVE plan
 * items already on the page -- never a second, drifting derivation and never
 * a stale server snapshot for a fact the page already holds live.
 *
 * FENCED CONTRACTS DELIBERATELY UNTOUCHED:
 *   - acceptedEstimateTotal (consult-close.ts) keeps its ruled >0 MONEY rule
 *     (adversarial finding F2: a zero total reads "no estimate recorded" on
 *     every MONEY surface, never "Rs 0 suggested") -- that is the HANDOFF
 *     money derivation. This module owns the accepted-STATE display, which
 *     must never collapse "accepted, but no fee recorded" into "nothing
 *     accepted".
 *   - The server deriveCourseProgress (lib/domain/clinical/treatment-journey
 *     .ts) stays THE course-grain owner for server consumers; this module
 *     mirrors its counting rule client-side over the SAME item facts the
 *     page renders (earlier-visit ledger rows + the live sheet), so the
 *     header counters can never lag the chip.
 *
 * C6 rides the same owner: a missing/zero fee is FLAGGED at add time on the
 * line and counted for the close panel ("No fee recorded on N items") --
 * no silent Rs-0 plans. Pure, React-free, unit-tested by direct call.
 */

import { planItemStatusLabel } from '@/dashboard/clinical-workbench/case-sheet-labels';
import { MID_DOT } from '@/dashboard/doctor/doctor-home-view';
import { rupeesLabel } from '@/dashboard/doctor/doctor-consult-view';

/** The minimal per-line facts the truth owner reads (the same fields the
 *  ledger rows already render -- live sheet items and course ledger rows
 *  both satisfy this shape). */
export interface AcceptedTruthItemFacts {
  readonly status: string;
  readonly estimatedValue: number | null;
  readonly completedAt?: string | null;
}

/** The ONE accepted-state truth for a set of plan lines. */
export interface AcceptedStateTruth {
  readonly itemCount: number;
  /** Lines the patient accepted (the decision axis -- value-independent). */
  readonly acceptedCount: number;
  /** Accepted lines with recorded completion evidence. */
  readonly completedCount: number;
  /** Accepted lines with NO completion evidence (they stay planned). */
  readonly remainingPlannedCount: number;
  /** Lines with no decision yet (advised / pending). */
  readonly undecidedCount: number;
  /** Sum of the ACCEPTED lines' recorded estimates (whole INR). null ONLY
   *  when no accepted line carries a recorded estimate at all; a true-zero
   *  sum stays 0 (the value axis -- never conflated with the decision axis). */
  readonly acceptedValuedSumInr: number | null;
  /** C6: lines whose fee is missing (null) or zero -- the no-silent-Rs-0
   *  guard's count, independent of catalog data hygiene. */
  readonly feeMissingCount: number;
}

/** Derive the accepted-state truth over plan lines. Pure and total. */
export function deriveAcceptedTruth(
  items: readonly AcceptedTruthItemFacts[],
): AcceptedStateTruth {
  let accepted = 0;
  let completed = 0;
  let undecided = 0;
  let feeMissing = 0;
  let valuedSum: number | null = null;
  for (const item of items) {
    if (planLineFeeMissing(item.estimatedValue)) feeMissing += 1;
    if (item.status === 'advised' || item.status === 'pending') undecided += 1;
    if (item.status !== 'accepted') continue;
    accepted += 1;
    if ((item.completedAt ?? null) !== null) completed += 1;
    if (typeof item.estimatedValue === 'number') {
      valuedSum = (valuedSum ?? 0) + item.estimatedValue;
    }
  }
  return {
    itemCount: items.length,
    acceptedCount: accepted,
    completedCount: completed,
    remainingPlannedCount: accepted - completed,
    undecidedCount: undecided,
    acceptedValuedSumInr: valuedSum,
    feeMissingCount: feeMissing,
  };
}

/** The ONE "Accepted - planned" chip label (line chip + course ledger chip
 *  read THIS, never an inline re-composition that can drift). The status
 *  word resolves through the existing dictionary. */
export function acceptedPlannedChipLabel(): string {
  return `${planItemStatusLabel('accepted')} ${MID_DOT} planned`;
}

/** C6: a line's fee is MISSING when no estimate is recorded (null) or the
 *  recorded estimate is zero. The amount still DISPLAYS per the W-B3 price
 *  truth (a stored 0 renders "Rs 0"); this flag makes it never SILENT. */
export function planLineFeeMissing(estimatedValue: number | null): boolean {
  return typeof estimatedValue !== 'number' || estimatedValue === 0;
}

/** The line-level flag copy (rendered beside the price at add time). */
export const NO_FEE_RECORDED_LABEL = 'No fee recorded';

/** The close-panel fee guard line: "No fee recorded on N item(s)." -- null
 *  when every line carries a non-zero fee (no warning owed). */
export function closeFeeMissingLine(truth: AcceptedStateTruth): string | null {
  if (truth.feeMissingCount === 0) return null;
  return `No fee recorded on ${truth.feeMissingCount} item${
    truth.feeMissingCount === 1 ? '' : 's'
  }.`;
}

/** The Estimation column's ACCEPTED cell display. The W-24 ratchet: an
 *  accepted line NEVER reads "Nothing accepted yet" -- a value-less accepted
 *  plan states BOTH facts ("1 accepted - no fee recorded") instead of letting
 *  the value axis silently overwrite the decision axis. */
export const NOTHING_ACCEPTED_LABEL = 'Nothing accepted yet';

export function acceptedCellDisplay(truth: AcceptedStateTruth): {
  readonly value: string;
  readonly dim: boolean;
} {
  if (truth.acceptedCount === 0) {
    return { value: NOTHING_ACCEPTED_LABEL, dim: true };
  }
  if (truth.acceptedValuedSumInr !== null && truth.acceptedValuedSumInr > 0) {
    return { value: rupeesLabel(truth.acceptedValuedSumInr), dim: false };
  }
  return {
    value: `${truth.acceptedCount} accepted ${MID_DOT} no fee recorded`,
    dim: true,
  };
}
